import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBomb, faRectangleHistory } from '@fortawesome/pro-solid-svg-icons';
import { SendsHistoryEntryType } from '../../../Api/StandaloneSendsEndpoints';
import SendsHistoryItem from '../SendsHistoryItem';
import StackVertical from '../../../Framework/Components/Wrappers/StackVertical';
import { FlexAlign, FlexGap, FlexJustify } from '../../../Framework/Data/Enums/LayoutEnums';
import InfiniteLoadTrigger from '../../../Framework/Components/InfiniteLoadTrigger';
import React, { useEffect, useState } from 'react';
import { useLayout } from '../../../Framework/Providers/LayoutProvider';
import { GetItemCounts } from '../../../Framework/Constants/DataHelpers';
import { StackedAddonPageHeader } from '../../../Framework/Components/LayoutScaffold/BasicPageHeader';
import SendsHistoryFilterControl, { SendHistoryFilterButton } from './SendsHistoryFilterControl';
import { useSendMailHistory } from '../../../Providers/SendMailHistoryProvider';
export type SendsHistoryControlType = {
	hideHeader?: boolean;
	forceHeader?: boolean;
	linkToSendsItem?: boolean;
	containerRef?: any;
};

const SendsHistoryControl = (props: SendsHistoryControlType) => {
	const { isTiny, isSmall, isMedium, isLarge } = useLayout();
	const { pageId, userId, status, data, error, isFetchingNextPage, fetchNextPage, hasNextPage } =
		useSendMailHistory();

	const [itemCounts, setItemCounts] = useState<any>({ loaded: 0, total: 0 });

	const ItemPlaceholder = () => {
		return (
			<div
				className={'content-loader content-fader'}
				style={{
					display: 'grid',
					alignItems: 'center',
					gap: 'var(--sm-unit)',
					margin: isMedium ? '0 var(--md-unit)' : 'var(--sm-unit)',
					gridTemplateColumns: isMedium
						? `6em repeat(${!!userId || !!pageId ? '4' : '5'}, 1fr)`
						: 'var(--thumbnail-size) 1fr',
				}}
			>
				{isMedium ? (
					<>
						<div style={{ width: 'var(--thumbnail-size)', aspectRatio: 1 }} />
						{!pageId && <div style={{ width: 'auto', height: '3em', marginBlock: 'var(--lg-unit)' }} />}
						{!userId && <div style={{ width: 'auto', height: '3em', marginBlock: 'var(--lg-unit)' }} />}
						<div style={{ width: 'auto', height: '3em', marginBlock: 'var(--lg-unit)' }} />
						<div style={{ width: 'auto', height: '3em', marginBlock: 'var(--lg-unit)' }} />
						<div style={{ width: 'auto', height: '3em', marginBlock: 'var(--lg-unit)' }} />
					</>
				) : (
					<>
						<div style={{ width: 'var(--thumbnail-size)', aspectRatio: 1 }} />
						<div style={{ width: 'auto', height: isTiny ? '8em' : isSmall ? '7em' : '6em' }} />
					</>
				)}
			</div>
		);
	};

	useEffect(() => {
		if (data) {
			setItemCounts(GetItemCounts(data));
		}
	}, [data]);

	return (
		<div
			className={props.hideHeader ? 'm-n-md rounded' : ''}
			style={{ overflow: props.hideHeader ? 'hidden' : 'auto' }}
		>
			{!props.hideHeader && (isLarge || props.forceHeader) && (
				<StackedAddonPageHeader
					title={'History'}
					addon={<SendHistoryFilterButton />}
					content={<SendsHistoryFilterControl />}
				/>
			)}

			{isMedium && (
				<div
					className={'item-row item-row-header pl-md pr-md'}
					style={{
						gridTemplateColumns: isMedium
							? !!userId || !!pageId
								? '6em 1fr 1fr 1fr 8em'
								: `6em 1fr 1fr 1fr 1fr 8em`
							: 'var(--thumbnail-size) 1fr',
					}}
				>
					<div className={'sm-sub-heading'}>File</div>
					{!pageId && <div className={'sm-sub-heading'}>Profile & ID</div>}
					{!userId && <div className={'sm-sub-heading'}>User</div>}
					<div className={'sm-sub-heading'}>Address</div>
					<div className={'sm-sub-heading'}>Postage</div>
					<div className={'sm-sub-heading'}>Date</div>
				</div>
			)}

			{status === 'loading' ? (
				<div className={'content-fader'}>
					<ItemPlaceholder />
					<ItemPlaceholder />
					<ItemPlaceholder />
					<ItemPlaceholder />
					<ItemPlaceholder />
					<ItemPlaceholder />
					<ItemPlaceholder />
				</div>
			) : status === 'error' ? (
				<StackVertical
					gap={FlexGap.ExtraSmall}
					align={FlexAlign.Center}
					justify={FlexJustify.Center}
					extraClass={'m-xxl p-xxl text-center'}
				>
					<FontAwesomeIcon icon={faBomb} size={'3x'} />
					<h6 className={'sub-heading mt-sm mb-xs'}>History</h6>
					<p>{error.message}</p>
				</StackVertical>
			) : (
				<>
					{data &&
						data.pages.map((group: any, groupIndex: number) =>
							group.data.map((entry: SendsHistoryEntryType, index: number) => (
								<SendsHistoryItem
									key={`sendsHistory${index}${groupIndex}`}
									{...entry}
									linkToSend={props.linkToSendsItem}
									hidePageDetails={!!pageId}
									hideUserDetails={!!userId}
								/>
							))
						)}

					{!data && (
						<StackVertical
							gap={FlexGap.ExtraSmall}
							align={FlexAlign.Center}
							justify={FlexJustify.Center}
							extraClass={'m-xxl p-xxl text-center'}
						>
							<FontAwesomeIcon icon={faRectangleHistory} size={'3x'} />
							<h6 className={'sub-heading mt-sm mb-xs'}>No History</h6>
							<p>Sends will start to show up here once they're sent </p>
						</StackVertical>
					)}

					{itemCounts.total === 0 && (
						<StackVertical
							gap={FlexGap.ExtraSmall}
							align={FlexAlign.Center}
							justify={FlexJustify.Center}
							extraClass={'m-xxl p-xxl text-center'}
						>
							<FontAwesomeIcon icon={faRectangleHistory} size={'3x'} />
							<h6 className={'sub-heading mt-sm mb-xs'}>No Matching Items</h6>
						</StackVertical>
					)}

					{itemCounts.total > 0 && (
						<InfiniteLoadTrigger
							ref={props.containerRef?.current}
							loaded={itemCounts.loaded}
							totalCount={itemCounts.total}
							hasNextPage={hasNextPage}
							fetchNextPage={fetchNextPage}
							isFetchingNextPage={isFetchingNextPage}
						/>
					)}
				</>
			)}
		</div>
	);
};

export default SendsHistoryControl;
